import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import "../../assets/css/login.css";
import {
  Backdrop,
  CircularProgress,
  OutlinedInput,
  Typography,
  Button,
  Box,
  Grid,
  Paper,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import jwtDecode from "jwt-decode";
import { GoogleLogin } from "@react-oauth/google";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { GetTimezoneInfoByTimezone } from "../../util/timezones";
import { SIGNUP_USER, SIGNUP_USER_GOOGLE } from "../../graphql/mutations";
import GettingStartedGuide from "../common/gettingStartedGuide";
import Logo from "../../assets/images/plany365.ico";
import { getSafeReturnTo, setToken } from "../../util/authUtils";
import { toFiniteIntOrNull } from "../../util/timezoneUtils";

function SignUp(props) {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timeZoneId = GetTimezoneInfoByTimezone(timeZone)?.id;
  const safeTimeZoneId = toFiniteIntOrNull(timeZoneId);
  const location = useLocation();
  const brandName = "MicroLogic";
  const searchParams = new URLSearchParams(location.search);

  // 👇 read and sanitize returnTo (if the user arrived here from a protected action)
  const rawReturnTo = searchParams.get("returnTo");
  const safeReturnTo = getSafeReturnTo(rawReturnTo);
  // Normalize once so it's always absolute or null
  const safeReturnToNormalized = safeReturnTo
    ? (safeReturnTo.startsWith("/") ? safeReturnTo : `/${safeReturnTo}`)
    : null;
  const stateFrom =
    typeof location.state?.from === "string"
      ? getSafeReturnTo(location.state.from)
      : null;
  const storedFrom = getSafeReturnTo(localStorage.getItem("redirectAfterLogin"));
  const currentPath = `${location.pathname}${location.search}${location.hash}`;
  const normalizePath = (path) => {
    if (!path) return null;
    try {
      return decodeURIComponent(path);
    } catch {
      return path;
    }
  };
  const isAuthPath = (path) =>
    !!normalizePath(path) &&
    /^\/(login|signup|forgotpassword)(?:[/?#]|$)/.test(normalizePath(path));
  const referrerPath = (() => {
    try {
      if (!document?.referrer) return null;
      const url = new URL(document.referrer);
      if (url.origin !== window.location.origin) return null;
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return null;
    }
  })();
  const brandHref = "/";
  const handleBrandClick = (e) => {
    e.preventDefault();
    navigate("/", { replace: true });
  };

  const initialEmail = searchParams.get("email") || "";
  const pathname = window.location.pathname;
  const isTrial = pathname.includes("start-free-trial");

  const [email, setEmail] = useState(initialEmail);
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [errors, setErrors] = useState([]);
  const [open, setOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const navigate = useNavigate();

  const [signUpUser] = useMutation(SIGNUP_USER);
  const [signUpWithGoogleMutation] = useMutation(SIGNUP_USER_GOOGLE);

  const handleClose = () => setOpen(false);

  const handleGuideClose = () => setGuideOpen(false);

  // ✅ Always navigate with an absolute path, and replace history
  const goto = (path) => {
    const absolute = path.startsWith("/") ? path : `/${path}`;
    navigate(absolute, { replace: true });
  };

  const completeGoogleSignUp = async ({ email: googleEmail, name }) => {
    const result = await signUpWithGoogleMutation({
      variables: {
        email: googleEmail,
        username: name || googleEmail,
        ...(safeTimeZoneId != null ? { timeZoneId: safeTimeZoneId } : {}),
      },
    });

    const token = result?.data?.signUpGoogle?.token;
    setToken(token);
    props.login(token);

    if (safeReturnToNormalized) {
      goto(safeReturnToNormalized);
    } else {
      goto("/biz/dashboard");
    }
  };

  const handleGoogleCredentialSuccess = async (credentialResponse) => {
    setErrors([]);
    try {
      const credential = credentialResponse?.credential;
      if (!credential) throw new Error("Missing Google credential.");
      const profile = jwtDecode(credential);
      if (!profile?.email) throw new Error("Google did not return an email.");
      await completeGoogleSignUp({ email: profile.email, name: profile.name });
    } catch (err) {
      if (err.message === "Validation error") {
        setErrors(["Existing account with provided email"]);
      } else {
        setErrors([err.message || "Sign up with Google failed"]);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = [];

    if (!fullName.trim()) validationErrors.push("Full Name is required");
    if (!email.trim()) {
      validationErrors.push("Email is required");
    } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email)) {
      validationErrors.push("Invalid Email Address");
    }
    if (!password.trim()) {
      validationErrors.push("Password is required");
    } else if (password.length < 6) {
      validationErrors.push("Password must be at least 6 characters long");
    }
    if (password !== passwordConfirm) {
      validationErrors.push("Passwords do not match");
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    signUpUser({
      variables: {
        email,
        password,
        username: fullName,
        ...(safeTimeZoneId != null ? { timeZoneId: safeTimeZoneId } : {}),
      },
    })
      .then((result) => {
        setOpen(true);
        setTimeout(() => {
          handleClose();

          // Save token + mark logged in
          const token = result?.data?.signUp?.token;
          setToken(token);
          props.login(token);

          // ✅ Use normalized absolute navigation
          if (safeReturnToNormalized) {
            goto(safeReturnToNormalized);
          } else {
            goto("/biz/dashboard"); // fallback page after sign-up
          }
        }, 500);
      })
      .catch((err) => setErrors([err.message]));
  };

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 100px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        px: 2,
        py: { xs: 3, sm: 4 },
        backgroundColor: "#ffffff",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 980,
          borderRadius: 4,
          border: "1px solid #dbe4ee",
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.12)",
          backgroundColor: "#ffffff",
          overflow: "hidden",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        }}
      >
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "space-between",
            p: 5,
            background:
              "linear-gradient(160deg, #0b1f33 0%, #15314c 58%, #1b4464 100%)",
            color: "#ffffff",
          }}
        >
          <Box
            component={RouterLink}
            to={brandHref}
            onClick={handleBrandClick}
            sx={{ display: "flex", alignItems: "center", gap: 1.5, color: "inherit", textDecoration: "none" }}
          >
            <Box
              component="img"
              src={Logo}
              alt={brandName}
              sx={{ width: 42, height: 42, borderRadius: 1, backgroundColor: "#fff" }}
            />
            <Typography sx={{ fontSize: 18, fontWeight: 800, letterSpacing: 0.4 }}>
              {brandName}
            </Typography>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 36, lineHeight: 1.1, fontWeight: 800, mb: 1.5 }}>
              {isTrial ? "Start your free 30-day trial." : "Create your professional workspace."}
            </Typography>
            <Typography sx={{ fontSize: 15, color: "rgba(255,255,255,0.85)", maxWidth: 360 }}>
              {isTrial
                ? "Sign up today and launch your workspace with no credit card required."
                : "Set up your account and open the product workspace that fits your needs."}
            </Typography>
          </Box>

          <Box
            sx={{
              border: "1px solid rgba(255,255,255,0.22)",
              borderRadius: 2,
              p: 2,
              backgroundColor: "rgba(255,255,255,0.10)",
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.95)" }}>
              Built for focused product workspaces
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
          <Typography
            component={RouterLink}
            to={brandHref}
            onClick={handleBrandClick}
            sx={{ fontSize: 13, fontWeight: 700, letterSpacing: 1, color: "#0891b2", mb: 1, textDecoration: "none", display: "inline-block" }}
          >
            {brandName}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: "#0f172a", mb: 1 }}>
            Create account
          </Typography>
          <Typography variant="body2" sx={{ color: "#475569", mb: 3 }}>
            Start managing your product workspace from one account.
          </Typography>

          <form onSubmit={handleSubmit} style={{ marginBottom: "16px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.75 }}>Email</Typography>
                <OutlinedInput
                  fullWidth
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{ borderRadius: 2, backgroundColor: "#fff" }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.75 }}>Full Name</Typography>
                <OutlinedInput
                  fullWidth
                  placeholder="Full Name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  sx={{ borderRadius: 2, backgroundColor: "#fff" }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.75 }}>Password</Typography>
                <OutlinedInput
                  fullWidth
                  placeholder="Password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  sx={{ borderRadius: 2, backgroundColor: "#fff" }}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.75 }}>Confirm Password</Typography>
                <OutlinedInput
                  fullWidth
                  placeholder="Confirm Password"
                  type={showPasswordConfirm ? "text" : "password"}
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  sx={{ borderRadius: 2, backgroundColor: "#fff" }}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPasswordConfirm((prev) => !prev)} edge="end">
                        {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </Grid>
            </Grid>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              fullWidth
              sx={{
                mt: 2.5,
                py: 1.25,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                fontSize: 15,
                background: "linear-gradient(135deg, #0369a1 0%, #0f766e 100%)",
              }}
            >
              Sign Up
            </Button>
          </form>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, my: 2.25 }}>
            <Box sx={{ flex: 1, height: 1, backgroundColor: "#dbe4ee" }} />
            <Typography sx={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>OR</Typography>
            <Box sx={{ flex: 1, height: 1, backgroundColor: "#dbe4ee" }} />
          </Box>

          <Box
            sx={{
              width: "100%",
              mb: 1,
              "& > div": { width: "100% !important" },
              "& iframe": { width: "100% !important" },
            }}
          >
            <GoogleLogin
              onSuccess={handleGoogleCredentialSuccess}
              onError={() => setErrors(["Google sign up failed. Try again."])}
              text="continue_with"
              shape="rectangular"
              size="large"
              width="100%"
              useOneTap={false}
            />
          </Box>
          {errors.length > 0 && (
            <Box>
              {errors.map((err, idx) => (
                <Typography
                  key={idx}
                  align="center"
                  sx={{
                    mb: 0.5,
                    color: "#b91c1c",
                    fontSize: 13,
                    backgroundColor: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  {err}
                </Typography>
              ))}
            </Box>
          )}

          <Backdrop open={open} style={{ zIndex: 9999, color: "#fff" }}>
            <CircularProgress />
            <Typography variant="h6" style={{ marginLeft: "10px" }}>
              Preparing your dashboard…
            </Typography>
          </Backdrop>

          <Typography align="center" variant="body2" sx={{ color: "#475569" }}>
            Already have an account? <a href="/login" style={{ fontWeight: 700 }}>Sign In</a>
          </Typography>
        </Box>
      </Paper>

      <GettingStartedGuide guideOpen={guideOpen} handleGuideClose={handleGuideClose} />
    </Box>
  );
}

export default SignUp;
